import type { COUNTRY_SHORTS } from '~/constants'
import type { SegmentTypes } from '~/types/api/rental-segment-types'
import type { getRvDetails } from '~/utils/rv/get-rv-details'

type ViewedListing = SegmentTypes['ListingSetViewed [v5]']['listings'][number]

export type TrackedListing = Omit<ViewedListing, 'distanceUnit'> & { distanceUnit: 'km' | 'mi' }

/**
 * Values that depend on the caller (composables, search state, formatting), so
 * they are passed in instead of being derived here.
 */
export type TrackedListingContext
  = Pick<TrackedListing, 'distanceUnit' | 'heroImage' | 'isFavourite' | 'nightlyRate'>
    & Partial<Pick<TrackedListing, 'distanceKm' | 'listPosition' | 'undatedTripEnd' | 'undatedTripStart'>>

/**
 * Maps RV details to the listing shape shared by the ListingSet* and
 * ListingClicked Segment events.
 *
 * Returns `undefined` when the RV has no (known) type. The API types `RVType` as
 * nullable, but Segment requires it, and no default would be truthful, so callers
 * should skip the event for that listing instead of guessing.
 *
 * @param details The result of `getRvDetails` for the RV.
 * @param context The caller specific values.
 * @returns The tracked listing, or `undefined` when it can't be reported accurately.
 */
export function toTrackedListing(
  details: ReturnType<typeof getRvDetails>,
  context: TrackedListingContext,
): TrackedListing | undefined {
  const {
    rvAverageRating,
    rvCity,
    rvCountry,
    rvGuests,
    rvHasDelivery,
    rvId,
    rvInstabookOwnerOptedIn,
    rvIsFeatured,
    rvNumberOfReview,
    rvRVName,
    rvRVType,
    rvState,
  } = details

  if (!rvRVType) {
    return undefined
  }

  const rentalType = getRvCategory(rvRVType)

  if (!rentalType) {
    return undefined
  }

  return {
    ...context,
    currency: lookupCountryCodeToCurrencyCode(rvCountry as COUNTRY_SHORTS),
    hasDelivery: rvHasDelivery,
    isFeatured: rvIsFeatured,
    isInstantBook: rvInstabookOwnerOptedIn,
    listingCity: rvCity ?? '',
    listingCountry: rvCountry as COUNTRY_SHORTS,
    listingRegion: rvState ?? '',
    listingSleepingSpots: rvGuests,
    listingType: rvRVType,
    numReviews: rvNumberOfReview,
    rentalType,
    reviewsShown: rvNumberOfReview > 0,
    rvId,
    rvName: rvRVName ?? '',
    starRating: rvAverageRating,
  }
}
